import { FeedbackResponseRow } from "@/components/feedback/types";

import {
  CITIZEN_SATISFACTION_COLLECTION_ID,
  GENERAL_FEEDBACK_COLLECTION_ID,
} from "./feedbackCollectionFixtures";

const USERS = [
  "Joe TheOtherUser",
  "Sally TheAdditionalUser",
  "Bob TheUser",
  "Alice TheReviewer",
  "Mike TheCaller",
  "Dana TheAnalyst",
];

const GROUPS = [
  "Metropolis Police Dept.",
  "Gotham Police Dept.",
  "Smallville Sheriff's Office",
  "Star City PD",
];

const AGENCIES = [
  "Gotham Police Department",
  "Metropolis Police Department",
  "Smallville Sheriff's Office",
  "Star City Police Department",
];

const STATUSES: FeedbackResponseRow["status"][] = [
  "in_review",
  "in_review",
  "in_review",
  "reviewed",
  "dismissed",
];

const pick = <T>(arr: T[], i: number): T => arr[i % arr.length];

const ONLINE_REPORTING_POSITIVE_COMMENTS = [
  "Submitting through the portal was painless. The summary at the end was great.",
  "Very easy to attach photos. Confirmation email came right away.",
  "I appreciated the autosave when I got interrupted mid-report.",
  "Clear language, no police jargon. Knew what I was filling out.",
];

const ONLINE_REPORTING_NEGATIVE_COMMENTS = [
  "I tried three times to upload a photo and it kept failing.",
  "Hard to find on the website. Took several searches.",
  "Questions felt repetitive, especially around the timeline.",
  "Page kept timing out. Had to start over twice.",
];

const CITIZEN_SERVICE_POSITIVE_COMMENTS = [
  "Officer was respectful and explained next steps clearly.",
  "Quick response, very professional throughout.",
  "I felt heard. Thank you for taking my concern seriously.",
  "Front desk staff were friendly even though I was upset.",
];

const CITIZEN_SERVICE_NEGATIVE_COMMENTS = [
  "Waited over an hour just to speak with someone.",
  "Felt like my issue was brushed off without explanation.",
  "Hard to get a callback after my initial visit.",
  "The phone tree made it hard to reach a person.",
];

// Build a populated surveyjs_data payload for the Citizen Satisfaction Feedback
// collection. Alternates satisfied / not satisfied with realistic reason picks.
const buildCitizenSatisfactionData = (i: number): Record<string, unknown> => {
  const isSatisfied = i % 3 !== 0;
  if (isSatisfied) {
    return {
      isSatisfied: true,
      "feedbackReasons-satisfied": pickReasons(
        [
          "Intuitive flow",
          "Clear questions",
          "Easy attachment upload",
          "Instant notifications",
        ],
        i,
      ),
      "feedbackDescription-satisfied": pick(
        ONLINE_REPORTING_POSITIVE_COMMENTS,
        i,
      ),
    };
  }
  return {
    isSatisfied: false,
    "feedbackReasons-unsatisfied": pickReasons(
      [
        "Confusing",
        "Slow",
        "Upload failed",
        "Technical issues",
        "Hard to find",
      ],
      i,
    ),
    "feedbackDescription-notSatisfied": pick(
      ONLINE_REPORTING_NEGATIVE_COMMENTS,
      i,
    ),
  };
};

const buildGeneralFeedbackData = (i: number): Record<string, unknown> => {
  const wasPositive = i % 3 !== 0;
  if (wasPositive) {
    return {
      wasPositive: true,
      positiveAspects: pickReasons(
        [
          "Professional staff",
          "Quick response",
          "Clear communication",
          "Respectful treatment",
        ],
        i,
      ),
      additionalComments: pick(CITIZEN_SERVICE_POSITIVE_COMMENTS, i),
    };
  }
  return {
    wasPositive: false,
    improvementAreas: pickReasons(
      ["Wait time", "Communication", "Staff demeanor", "Outcome"],
      i,
    ),
    additionalComments: pick(CITIZEN_SERVICE_NEGATIVE_COMMENTS, i),
  };
};

// Pick a deterministic subset of reasons based on index — gives variety across rows.
const pickReasons = (options: string[], i: number): string[] => {
  const count = (i % 3) + 1;
  const start = i % options.length;
  return Array.from(
    { length: count },
    (_, k) => options[(start + k) % options.length],
  );
};

export const FEEDBACK_FIXTURES: FeedbackResponseRow[] = Array.from(
  { length: 24 },
  (_, i) => {
    const isCitizenSatisfaction = i % 2 === 0;
    const collectionId = isCitizenSatisfaction
      ? CITIZEN_SATISFACTION_COLLECTION_ID
      : GENERAL_FEEDBACK_COLLECTION_ID;
    const surveyjs_data = isCitizenSatisfaction
      ? buildCitizenSatisfactionData(i)
      : buildGeneralFeedbackData(i);

    // time_updated is the most recent timestamp; created/submitted precede it
    // so the lifecycle reads created → submitted → updated.
    const updated = new Date("2026-03-11T06:40:38Z");
    updated.setHours(updated.getHours() - i * 3);
    const submitted = new Date(updated);
    submitted.setHours(submitted.getHours() - 1);
    const created = new Date(updated);
    created.setHours(created.getHours() - 2);
    const expiry = new Date(updated);
    expiry.setDate(expiry.getDate() + 2);
    return {
      id: String(3343 + i),
      feedback_collection_id: collectionId,
      status: pick(STATUSES, i),
      user_display_name: pick(USERS, i),
      intended_group: pick(GROUPS, i),
      agency: pick(AGENCIES, i),
      time_created: created.toISOString(),
      time_updated: updated.toISOString(),
      time_submitted: submitted.toISOString(),
      time_modification_token_exp: expiry.toISOString(),
      surveyjs_data,
    };
  },
);
